import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { MenuSection } from "../components/MenuSection";
import { Phone, Mail, ArrowDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

export default function CartePage({
  data,
  heroImg,
  testid,
  galleryImgs = [],
  subtitleKey,
  titleKey,
}) {
  const { t } = useLang();
  const subtitle = subtitleKey ? t.carteSubtitles[subtitleKey] : data.subtitle;
  const localizedTitle = titleKey ? t.nav[titleKey] : (t.sections[data.title] || data.title);
  const titleWords = localizedTitle.split(" ");
  const lastWord = titleWords.pop();
  const firstWords = titleWords.join(" ");

  // Insert gallery images every N sections
  const sectionsWithImages = [];
  const interval = Math.max(3, Math.ceil(data.sections.length / (galleryImgs.length + 1)));
  let imgIdx = 0;
  data.sections.forEach((s, i) => {
    sectionsWithImages.push({ type: "section", section: s, index: i });
    if (s.img) {
      sectionsWithImages.push({ type: "image", src: s.img, key: `secimg-${i}` });
    }
    if ((i + 1) % interval === 0 && imgIdx < galleryImgs.length) {
      sectionsWithImages.push({ type: "image", src: galleryImgs[imgIdx], key: `img-${imgIdx}` });
      imgIdx++;
    }
  });

  return (
    <div data-testid={testid} className="page-in min-h-screen" style={{ background: "var(--color-bg)" }}>
      <Header />

      <section className="relative pt-40 pb-20 sm:pt-48 sm:pb-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <p
              className="text-[11px] tracking-[0.4em] uppercase mb-6"
              style={{ color: "var(--color-text-muted)" }}
            >
              La Caravelle · Narbonne
            </p>
            <h1
              className="text-5xl sm:text-7xl md:text-[6rem] leading-[0.95] font-[\'Playfair_Display\'] tracking-tight"
              style={{ color: "var(--color-dark)" }}
            >
              {firstWords}{" "}
              <span className="italic opacity-80">{lastWord}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed" style={{ color: "var(--color-text-muted)" }}>
              {subtitle}
            </p>
            <a href="#sections" className="mt-10 inline-flex btn-pill btn-dark" data-testid="scroll-to-sections">
              {t.common.explore} <ArrowDown size={15} />
            </a>
          </Reveal>

          <Reveal className="lg:col-span-5" delay={120}>
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
              <img
                src={heroImg}
                alt={localizedTitle}
                className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-105"
              />
              <div className="absolute bottom-4 left-4 right-4 glass-light rounded-2xl px-5 py-3 flex items-center justify-between">
                <p className="text-xs tracking-[0.25em] uppercase" style={{ color: "var(--color-text-muted)" }}>
                  {t.common.cardLabel}
                </p>
                <p className="font-[\'Playfair_Display\'] text-lg" style={{ color: "var(--color-dark)" }}>
                  {localizedTitle}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="sections" className="py-12 sm:py-16">
        <main className="max-w-6xl mx-auto px-6 lg:px-8" data-testid="carte-main">
          {sectionsWithImages.map((entry) => {
            if (entry.type === "section") {
              return (
                <MenuSection
                  key={entry.section.name}
                  section={entry.section}
                  index={entry.index}
                />
              );
            }
            return (
              <Reveal key={entry.key} className="mb-16 sm:mb-24">
                <div
                  data-testid={`carte-gallery-${entry.key}`}
                  className="relative w-full aspect-[4/5] sm:aspect-[16/6] rounded-3xl overflow-hidden"
                >
                  <img
                    src={entry.src}
                    alt=""
                    data-testid="carte-gallery-image"
                    className="w-full h-full object-cover transition-transform duration-[1800ms] hover:scale-105"
                  />
                  <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(44,30,22,0) 50%, rgba(44,30,22,0.45) 100%)",
                    }}
                  />
                  <div className="absolute bottom-5 left-5 sm:bottom-7 sm:left-7">
                    <span
                      className="inline-block px-4 py-2 glass-light rounded-full text-[10px] tracking-[0.3em] uppercase"
                      style={{ color: "var(--color-dark)" }}
                    >
                      {localizedTitle}
                    </span>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </main>
      </section>

      <section className="relative overflow-hidden" style={{ background: "var(--color-dark)", color: "var(--color-bg)" }}>
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 sm:py-28 text-center">
          <Reveal>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-[\'Playfair_Display\']">
              {t.common.bookTable}{" "}
              <span className="italic opacity-80">{t.common.bookTableItalic}</span>
            </h2>
            <p className="mt-5 opacity-75 max-w-xl mx-auto">{t.common.openEveryday}</p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <a href="tel:0468325017" data-testid="carte-cta-phone" className="btn-pill btn-light">
                <Phone size={15} /> 04 68 32 50 17
              </a>
              <a
                href="mailto:lacaravellenarbonne@gmail.com"
                data-testid="carte-cta-email"
                className="btn-pill"
                style={{
                  background: "transparent",
                  color: "var(--color-bg)",
                  border: "1px solid rgba(246,244,240,0.3)",
                }}
              >
                <Mail size={15} /> {t.common.writeUs}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
